import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import useAxiosAuth from "../hooks/useAxiosAuth";

// The PLATFORM Users module: the Super Admin's own team (`/platform-staff`)
// and the platform roles they hold (`/platform-roles`) — the platform-level
// twin of the tenant app's staffSlice. One slice because they are one screen's
// worth of work: you cannot create a user without picking a role, and the role
// list is only ever read in order to assign one.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

// ------------------------------------------------------------ platform staff

export const getPlatformStaffList = createAsyncThunk(
    'platformStaff/getPlatformStaffList',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', platformRoleId = [], page = 1, limit = 10 } = params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            if (platformRoleId?.length) qs.append('platformRoleId', platformRoleId.join(','));
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/platform-staff?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// The signed-in platform staff member's OWN row (name, image, role) — keyed on
// the token, not on an id from storage, so one agent cannot load another's
// profile by editing localStorage. Read by the sidebar's user card.
export const getMyPlatformProfile = createAsyncThunk(
    'platformStaff/getMyPlatformProfile',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/platform-staff/me`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const getPlatformStaffById = createAsyncThunk(
    'platformStaff/getPlatformStaffById',
    async (platformStaffId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/platform-staff/${platformStaffId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const createPlatformStaff = createAsyncThunk(
    'platformStaff/createPlatformStaff',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/platform-staff`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const updatePlatformStaff = createAsyncThunk(
    'platformStaff/updatePlatformStaff',
    async ({ platformStaffId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/platform-staff/${platformStaffId}`, data);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const setPlatformStaffStatus = createAsyncThunk(
    'platformStaff/setPlatformStaffStatus',
    async ({ platformStaffId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/platform-staff/${platformStaffId}/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const deletePlatformStaff = createAsyncThunk(
    'platformStaff/deletePlatformStaff',
    async (platformStaffId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/platform-staff/${platformStaffId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Admin-initiated reset. No password goes either way (owner, 2 Oct 2026): the
// server locks the current one and tells the person to use Forgot password;
// the answer says whether that message went out (data.noticeDelivered).
export const resetPlatformStaffPassword = createAsyncThunk(
    'platformStaff/resetPlatformStaffPassword',
    async ({ platformStaffId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/platform-staff/${platformStaffId}/password`, {});
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ------------------------------------------------------------ platform roles

export const getPlatformRoles = createAsyncThunk(
    'platformStaff/getPlatformRoles',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', page = 1, limit = 50 } = params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/platform-roles?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const createPlatformRole = createAsyncThunk(
    'platformStaff/createPlatformRole',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/platform-roles`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const updatePlatformRole = createAsyncThunk(
    'platformStaff/updatePlatformRole',
    async ({ platformRoleId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/platform-roles/${platformRoleId}`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const setPlatformRoleStatus = createAsyncThunk(
    'platformStaff/setPlatformRoleStatus',
    async ({ platformRoleId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/platform-roles/${platformRoleId}/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const deletePlatformRole = createAsyncThunk(
    'platformStaff/deletePlatformRole',
    async (platformRoleId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/platform-roles/${platformRoleId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

/**
 * The permission catalogue — what a platform role CAN be granted. Fetched,
 * never bundled: the backend owns config/platformPermissions.js, and mirroring
 * is how data/accessControls.js drifted from its backend twin.
 */
export const getPlatformPermissionCatalogue = createAsyncThunk(
    'platformStaff/getPlatformPermissionCatalogue',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/platform-roles/catalogue`);
            return response.data?.groups || [];
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const platformStaffSlice = createSlice({
    name: "platformStaff",
    initialState: {
        staffList: [],
        staffTotal: 0,
        staffTotalPages: 1,
        staffListLoading: false,
        selectedStaff: null,
        // The signed-in platform staff member (null for the super admin).
        myProfile: null,

        roles: [],
        rolesLoading: false,

        catalogue: [],
        catalogueLoading: false,

        mutating: false,
        error: null,
    },
    reducers: {
        resetPlatformStaffState: (state) => {
            state.staffList = [];
            state.staffTotal = 0;
            state.staffTotalPages = 1;
            state.selectedStaff = null;
            state.error = null;
        },
        clearPlatformStaffError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPlatformStaffList.pending, (state) => { state.staffListLoading = true; state.error = null; })
            .addCase(getPlatformStaffList.fulfilled, (state, action) => {
                state.staffListLoading = false;
                state.staffList = action.payload?.staff || [];
                state.staffTotal = action.payload?.total || 0;
                state.staffTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(getPlatformStaffList.rejected, (state, action) => {
                state.staffListLoading = false;
                state.error = action.payload?.error || 'Failed to load platform users';
            })

            .addCase(getPlatformStaffById.fulfilled, (state, action) => { state.selectedStaff = action.payload; })
            .addCase(getMyPlatformProfile.fulfilled, (state, action) => { state.myProfile = action.payload; })

            .addCase(getPlatformRoles.pending, (state) => { state.rolesLoading = true; })
            .addCase(getPlatformRoles.fulfilled, (state, action) => {
                state.rolesLoading = false;
                state.roles = action.payload?.roles || [];
            })
            .addCase(getPlatformRoles.rejected, (state, action) => {
                state.rolesLoading = false;
                state.error = action.payload?.error || 'Failed to load platform roles';
            })

            .addCase(getPlatformPermissionCatalogue.pending, (state) => { state.catalogueLoading = true; })
            .addCase(getPlatformPermissionCatalogue.fulfilled, (state, action) => {
                state.catalogueLoading = false;
                state.catalogue = action.payload || [];
            })
            .addCase(getPlatformPermissionCatalogue.rejected, (state) => { state.catalogueLoading = false; })

            // Every mutation shares one flag; the pages refetch on success.
            .addMatcher(
                (a) => a.type.startsWith('platformStaff/') && a.type.endsWith('/pending') &&
                    /create|update|delete|setPlatformStaffStatus|setPlatformRoleStatus|resetPlatformStaffPassword/.test(a.type),
                (state) => { state.mutating = true; state.error = null; }
            )
            .addMatcher(
                (a) => a.type.startsWith('platformStaff/') && a.type.endsWith('/fulfilled') &&
                    /create|update|delete|setPlatformStaffStatus|setPlatformRoleStatus|resetPlatformStaffPassword/.test(a.type),
                (state) => { state.mutating = false; }
            )
            .addMatcher(
                (a) => a.type.startsWith('platformStaff/') && a.type.endsWith('/rejected') &&
                    /create|update|delete|setPlatformStaffStatus|setPlatformRoleStatus|resetPlatformStaffPassword/.test(a.type),
                (state, action) => {
                    state.mutating = false;
                    state.error = action.payload?.error || 'Action failed';
                }
            );
    },
});

export const { resetPlatformStaffState, clearPlatformStaffError } = platformStaffSlice.actions;
export default platformStaffSlice.reducer;
